import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CONTACT_CARDS_PAGE_SIZE } from "../contactCardsManageConstants";

/**
 * Normalizes the API list response into a plain array.
 * The endpoint returns { data: [...] } as the canonical envelope.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeList(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.contact_cards)) return data.contact_cards;
    return [];
}

/**
 * Performs the GET request and pushes results through setters.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchListPage({ page, signal, setItems, setHasMore, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`/admin/contact_cards/per_page/${page}`);
        if (signal.aborted) return;
        const items = normalizeList(data);
        setItems(items);
        setHasMore(items.length >= CONTACT_CARDS_PAGE_SIZE);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les contacts");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook returning the paginated contact cards list state for the given page.
 * Refetches on page change and exposes refresh for post-create sync.
 * @param {number} page - 1-indexed page number
 * @returns {{ items: Array, hasMore: boolean, loading: boolean, error: string|null, refresh: Function }}
 */
function useContactCardsList(page) {
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        fetchListPage({ page, signal: controller.signal, setItems, setHasMore, setError, setLoading });
        return () => controller.abort();
    }, [page, tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);
    return { items, hasMore, loading, error, refresh };
}

export default useContactCardsList;
